import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import { api, fileUrl } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Plus, Trash2, Upload, Wand2 } from "lucide-react";
import { toast } from "sonner";
import PhotoEditor from "../components/PhotoEditor";
import Media from "../components/Media";

const ModelDashboard = () => {
  const [angel, setAngel] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [wallText, setWallText] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState(null); // 'main' | 'gallery' | 'wall' | 'story'
  const [pendingFile, setPendingFile] = useState(null);
  const galleryInput = useRef(null);
  const galleryMultiInput = useRef(null);
  const mainInput = useRef(null);
  const wallInput = useRef(null);
  const storyInput = useRef(null);
  const receiptInput = useRef(null);
  const [receiptTier, setReceiptTier] = useState("standard");

  const load = async () => {
    const a = await api.get("/me/angel");
    setAngel(a.data);
    const r = await api.get("/me/receipts");
    setReceipts(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (changes) => {
    const r = await api.put("/me/angel", changes);
    setAngel(r.data);
    toast.success("Guardado");
  };

  const toggleAvailable = async () => {
    const r = await api.post("/me/angel/availability");
    setAngel((a) => ({ ...a, is_available: r.data.is_available }));
  };

  const openEditor = (file, target) => {
    // Video files bypass the photo editor (no canvas pixel-edit)
    if (file?.type?.startsWith("video/")) {
      uploadDirect(file, target);
      return;
    }
    setPendingFile(file);
    setEditorTarget(target);
    setEditorOpen(true);
  };

  const uploadDirect = async (file, target) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      if (target === "gallery") {
        const r = await api.post("/me/angel/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAngel((a) => ({ ...a, gallery: [...(a.gallery || []), r.data.path] }));
        toast.success("Video añadido a galería");
      } else if (target === "wall") {
        fd.append("text", wallText);
        await api.post("/me/wall/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setWallText("");
        toast.success("Publicado en tu muro");
      } else if (target === "story") {
        await api.post("/me/stories", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Historia publicada (24h)");
        loadStoryStatus();
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al subir");
    }
  };

  const handleEditorSave = async (blob) => {
    setEditorOpen(false);
    const fd = new FormData();
    fd.append("file", blob, "image.jpg");
    try {
      if (editorTarget === "main") {
        const r = await api.post("/me/angel/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAngel((a) => ({ ...a, main_photo_path: r.data.path }));
        toast.success("Foto principal actualizada");
      } else if (editorTarget === "gallery") {
        const r = await api.post("/me/angel/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAngel((a) => ({ ...a, gallery: [...(a.gallery || []), r.data.path] }));
        toast.success("Foto añadida a galería");
      } else if (editorTarget === "wall") {
        fd.append("text", wallText);
        await api.post("/me/wall/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setWallText("");
        toast.success("Publicado en tu muro");
      } else if (editorTarget === "story") {
        await api.post("/me/stories", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Historia publicada (24h)");
        loadStoryStatus();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al subir");
    }
  };

  const [storyStatus, setStoryStatus] = useState({ tier: "standard", limit: 1, used: 0 });
  const loadStoryStatus = async () => {
    try { const r = await api.get("/me/stories/status"); setStoryStatus(r.data); } catch (e) {}
  };
  useEffect(() => { loadStoryStatus(); }, [angel?.tier]);

  const postText = async () => {
    if (!wallText.trim()) return;
    try {
      await api.post("/me/wall", { text: wallText });
      setWallText("");
      toast.success("Publicado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const deleteGallery = async (path) => {
    await api.delete(`/me/angel/gallery?path=${encodeURIComponent(path)}`);
    setAngel((a) => ({ ...a, gallery: a.gallery.filter((p) => p !== path) }));
  };

  const uploadGalleryMulti = async (files) => {
    if (!files || !files.length) return;
    const arr = Array.from(files);
    toast.message(`Subiendo ${arr.length} foto(s)…`);
    let ok = 0;
    for (const f of arr) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/me/angel/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAngel((a) => ({ ...a, gallery: [...(a.gallery || []), r.data.path] }));
        ok++;
      } catch (e) { /* skip */ }
    }
    toast.success(`${ok}/${arr.length} fotos subidas`);
  };

  const uploadReceipt = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tier", receiptTier);
    await api.post("/me/receipts", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Comprobante enviado. Esperando aprobación.");
    load();
  };

  if (!angel) return <div><Header /><div className="p-10 text-center">Cargando…</div></div>;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#6B6A63]">Tu perfil de Angel</div>
            <h1 className="font-serif text-4xl text-[#1A2118]">{angel.artistic_name}</h1>
            <div className="text-sm mt-1">
              Estado: {angel.is_published ? <span className="text-[#22C55E]">Publicado</span> : <span className="text-[#6B6A63]">Oculto (pendiente de aprobación)</span>}
              {angel.expires_at && angel.is_published && <span className="text-[#6B6A63]"> · Vence {new Date(angel.expires_at).toLocaleDateString("es-CL")}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Disponible ahora</Label>
            <Switch data-testid="toggle-available" checked={angel.is_available} onCheckedChange={toggleAvailable} />
          </div>
        </div>

        <Tabs defaultValue="profile">
          <TabsList className="bg-[#F3F1EA] flex flex-wrap">
            <TabsTrigger data-testid="tab-profile" value="profile">Perfil</TabsTrigger>
            <TabsTrigger data-testid="tab-photos" value="photos">Fotos</TabsTrigger>
            <TabsTrigger data-testid="tab-wall" value="wall">Muro</TabsTrigger>
            <TabsTrigger data-testid="tab-stories" value="stories">Historia 24h</TabsTrigger>
            <TabsTrigger data-testid="tab-pay" value="pay">Pago</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="pt-6">
            <ProfileEditor angel={angel} onSave={save} />
          </TabsContent>

          <TabsContent value="photos" className="pt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="text-sm font-medium mb-2">Foto principal</div>
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-[#F3F1EA] border border-[#E5E2D9]">
                  {angel.main_photo_path && (
                    <img src={fileUrl(angel.main_photo_path)} className="w-full h-full object-cover" alt="" />
                  )}
                  <div className="cherub-watermark absolute inset-0" />
                </div>
                <input ref={mainInput} type="file" accept="image/*" className="hidden"
                       onChange={(e) => e.target.files?.[0] && openEditor(e.target.files[0], "main")} />
                <button data-testid="upload-main" onClick={() => mainInput.current?.click()} className="btn-primary mt-3 inline-flex items-center gap-2">
                  <Wand2 size={16} /> Editar y subir
                </button>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium">Galería</div>
                  <div className="flex gap-2">
                    <input ref={galleryInput} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden"
                           onChange={(e) => e.target.files?.[0] && openEditor(e.target.files[0], "gallery")} />
                    <input ref={galleryMultiInput} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" multiple className="hidden"
                           onChange={(e) => uploadGalleryMulti(e.target.files)} />
                    <button data-testid="upload-gallery" onClick={() => galleryInput.current?.click()} className="btn-ghost text-sm inline-flex items-center gap-1">
                      <Plus size={14} /> Editar y subir
                    </button>
                    <button data-testid="upload-gallery-multi" onClick={() => galleryMultiInput.current?.click()} className="btn-ghost text-sm inline-flex items-center gap-1">
                      <Upload size={14} /> Subir varias
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(angel.gallery || []).map((p) => (
                    <div key={p} className="relative aspect-[9/16] rounded-lg overflow-hidden bg-[#F3F1EA] no-save">
                      <Media path={p} />
                      <button onClick={() => deleteGallery(p)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 hover:bg-white">
                        <Trash2 size={14} className="text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="wall" className="pt-6 space-y-3">
            <Textarea
              data-testid="wall-text"
              value={wallText}
              onChange={(e) => setWallText(e.target.value)}
              placeholder="Comparte algo con tus seguidoras (sin enlaces externos)…"
              className="bg-white"
            />
            <div className="flex gap-2">
              <button data-testid="post-text" onClick={postText} className="btn-primary">Publicar texto</button>
              <input ref={wallInput} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden"
                     onChange={(e) => e.target.files?.[0] && openEditor(e.target.files[0], "wall")} />
              <button data-testid="post-image" onClick={() => wallInput.current?.click()} className="btn-ghost inline-flex items-center gap-1">
                <Upload size={16} /> Subir foto o video
              </button>
            </div>
            <div className="text-xs text-[#6B6A63]">Las publicaciones se muestran en tu perfil en tiempo real.</div>
          </TabsContent>

          <TabsContent value="stories" className="pt-6 space-y-3">
            <div className="border border-[#E5E2D9] rounded-xl bg-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Tu plan: <span className="capitalize">{storyStatus.tier}</span></div>
                  <div className="text-xs text-[#6B6A63]">
                    Límite diario: {storyStatus.limit === 9999 ? "Sin límite" : `${storyStatus.used} / ${storyStatus.limit}`}
                  </div>
                </div>
                <input ref={storyInput} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden"
                       onChange={(e) => e.target.files?.[0] && openEditor(e.target.files[0], "story")} />
                <button data-testid="upload-story" onClick={() => storyInput.current?.click()}
                        disabled={storyStatus.limit !== 9999 && storyStatus.used >= storyStatus.limit}
                        className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  <Upload size={16} /> Subir historia
                </button>
              </div>
              <p className="text-xs text-[#6B6A63] mt-3">Las historias desaparecen tras 24 horas. Standard: 1/día · Gold: 3/día · Premium: ilimitado.</p>
            </div>
          </TabsContent>

          <TabsContent value="pay" className="pt-6 space-y-4">
            {/* Pricing tier cards */}
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { key: "standard", name: "Standard", price: 50000, perks: ["Aparición en catálogo", "1 historia / día"] },
                { key: "gold", name: "Gold", price: 75000, perks: ["Borde plateado", "3 historias / día", "Mejor posición"] },
                { key: "premium", name: "Premium", price: 100000, perks: ["Borde dorado iluminado", "Historias ilimitadas", "Top de la página"] },
              ].map((p) => (
                <button
                  key={p.key}
                  data-testid={`tier-${p.key}`}
                  type="button"
                  onClick={() => setReceiptTier(p.key)}
                  className={`text-left border rounded-xl p-4 bg-white transition ${receiptTier === p.key ? 'border-[#364531] shadow-md ring-2 ring-[#364531]/15' : 'border-[#E5E2D9] hover:border-[#364531]/40'}`}
                >
                  <div className={`text-xs uppercase tracking-widest ${p.key==='premium'?'text-[#D4AF37]':p.key==='gold'?'text-[#A8A9AD]':'text-[#6B6A63]'}`}>{p.name}</div>
                  <div className="font-serif text-2xl mt-1">{fmtCLP(p.price)}<span className="text-xs text-[#6B6A63]"> /semana</span></div>
                  <ul className="mt-2 text-xs text-[#6B6A63] space-y-1">
                    {p.perks.map((k) => <li key={k}>· {k}</li>)}
                  </ul>
                </button>
              ))}
            </div>

            <div className="border border-[#E5E2D9] bg-white rounded-xl p-4">
              <div className="font-serif text-xl mb-2">Datos de transferencia</div>
              <div className="text-sm text-[#6B6A63] mb-3">Transfiere a la cuenta indicada y sube el comprobante. El admin activará tu perfil tras la validación.</div>
              <div className="bg-[#F3F1EA] rounded-lg p-3 text-sm">
                <div><strong>Banco:</strong> BancoEjemplo</div>
                <div><strong>Cuenta:</strong> 1234567-8</div>
                <div><strong>RUT:</strong> 11.111.111-1</div>
                <div><strong>Email:</strong> pagos@secretangels.com</div>
                <div className="mt-2 pt-2 border-t border-[#E5E2D9]">
                  Monto a transferir: <strong>{fmtCLP(TIER_PRICES[receiptTier])}</strong> ({receiptTier})
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-sm">Comprobante de pago</Label>
                <input ref={receiptInput} type="file" accept="image/*,application/pdf" className="hidden"
                       onChange={(e) => e.target.files?.[0] && uploadReceipt(e.target.files[0])} />
                <button data-testid="upload-receipt" onClick={() => receiptInput.current?.click()} className="mt-1 btn-primary w-full inline-flex items-center justify-center gap-2">
                  <Upload size={16} /> Subir comprobante para plan {receiptTier}
                </button>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium mb-2">Tus comprobantes</div>
              <div className="border border-[#E5E2D9] rounded-xl bg-white">
                {receipts.map((r) => (
                  <div key={r.receipt_id} className="flex items-center justify-between px-4 py-3 border-b border-[#E5E2D9] last:border-b-0">
                    <div className="text-sm">
                      <div>{new Date(r.created_at).toLocaleString("es-CL")} · Plan {r.tier_requested}</div>
                      <div className="text-xs text-[#6B6A63]">Estado: {r.status}</div>
                    </div>
                    <a href={fileUrl(r.path)} target="_blank" rel="noreferrer" className="text-sm text-[#364531] underline">Ver</a>
                  </div>
                ))}
                {!receipts.length && <div className="px-4 py-3 text-sm text-[#6B6A63]">Aún no has enviado comprobantes.</div>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <PhotoEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        file={pendingFile}
        onSave={handleEditorSave}
      />
    </div>
  );
};

const SERVICES_LIST = [
  "Masajes erótico 30 minutos",
  "Masajes erótico 1 hora",
  "Venta Pack contenido virtual",
  "Videollamadas 5 minutos",
  "Videollamadas 10 minutos",
  "Escort momento",
  "Escort 30 minutos",
  "Escort 1 hora",
];
const ATTR_FIELDS = ["Estatura", "Cabello", "Ojos", "Nacionalidad", "Medidas", "Piel", "Depilación"];
const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const TIER_PRICES = { premium: 100000, gold: 75000, standard: 50000 };
const fmtCLP = (n) => "CLP $" + (n || 0).toLocaleString("es-CL");

const ProfileEditor = ({ angel, onSave }) => {
  const [form, setForm] = useState({
    artistic_name: angel.artistic_name || "",
    age: angel.age || "",
    comuna: angel.comuna || "",
    bio: angel.bio || "",
    whatsapp: angel.whatsapp || "",
    address: angel.address || "",
  });
  const [attrs, setAttrs] = useState(angel.attributes || {});
  const [services, setServices] = useState(angel.services || []);
  const [additionals, setAdditionals] = useState(angel.additionals || []);
  const [schedule, setSchedule] = useState(angel.schedule || []);

  const submit = () => {
    onSave({
      ...form,
      age: form.age ? Number(form.age) : null,
      attributes: attrs,
      services,
      additionals,
      schedule,
    });
  };

  const toggleService = (name) => {
    setServices((arr) => {
      const exists = arr.find((s) => s.name === name);
      if (exists) return arr.filter((s) => s.name !== name);
      return [...arr, { name, price: "" }];
    });
  };
  const updateServicePrice = (name, price) => {
    setServices((arr) => arr.map((s) => s.name === name ? { ...s, price } : s));
  };

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-3">
        <div><Label>Nombre artístico</Label><Input data-testid="input-name" value={form.artistic_name} onChange={(e) => setForm((f) => ({ ...f, artistic_name: e.target.value }))} /></div>
        <div><Label>Edad</Label><Input data-testid="input-age" type="number" value={form.age} onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))} /></div>
        <div><Label>Comuna</Label><Input data-testid="input-comuna" value={form.comuna} onChange={(e) => setForm((f) => ({ ...f, comuna: e.target.value }))} /></div>
        <div><Label>WhatsApp (con código país)</Label><Input data-testid="input-wa" value={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} placeholder="+56912345678" /></div>
      </div>
      <div>
        <Label>Bio</Label>
        <Textarea data-testid="input-bio" value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} />
      </div>
      <div>
        <Label>Dirección / Sector (para el mapa)</Label>
        <Input data-testid="input-address" placeholder="Ej: Las Condes, Santiago" value={form.address}
               onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
      </div>

      {/* Atributos */}
      <div className="border border-[#E5E2D9] rounded-xl p-4 bg-white">
        <div className="text-sm font-medium mb-2">Atributos</div>
        <div className="grid md:grid-cols-2 gap-3">
          {ATTR_FIELDS.map((k) => (
            <div key={k}>
              <Label className="text-xs">{k}</Label>
              <Input data-testid={`attr-${k}`} value={attrs[k] || ""} onChange={(e) => setAttrs((a) => ({ ...a, [k]: e.target.value }))} />
            </div>
          ))}
        </div>
      </div>

      {/* Servicios (predefinidos) */}
      <div className="border border-[#E5E2D9] rounded-xl p-4 bg-white">
        <div className="text-sm font-medium mb-3">Servicios ofrecidos</div>
        <div className="space-y-2">
          {SERVICES_LIST.map((name) => {
            const sel = services.find((x) => x.name === name);
            return (
              <div key={name} className="grid grid-cols-12 gap-2 items-center">
                <label className="col-span-7 flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`svc-${name}`}
                    checked={!!sel}
                    onChange={() => toggleService(name)}
                    className="h-4 w-4 accent-[#364531]"
                  />
                  {name}
                </label>
                <Input
                  className="col-span-5"
                  placeholder="Tarifa (opcional)"
                  disabled={!sel}
                  value={sel?.price || ""}
                  onChange={(e) => updateServicePrice(name, e.target.value)}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Adicionales libres */}
      <div className="border border-[#E5E2D9] rounded-xl p-4 bg-white">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">Adicionales</div>
          <button type="button" onClick={() => setAdditionals((s) => [...s, { name: "", price: "" }])} className="btn-ghost text-sm">+ Añadir</button>
        </div>
        <p className="text-xs text-[#6B6A63] mb-2">Servicios extra que tú ofrezcas. La tarifa es opcional.</p>
        {additionals.map((a, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2">
            <Input className="col-span-7" placeholder="Servicio (libre)" value={a.name}
                   onChange={(e) => setAdditionals((arr) => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
            <Input className="col-span-4" placeholder="Tarifa (opcional)" value={a.price}
                   onChange={(e) => setAdditionals((arr) => arr.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} />
            <button className="col-span-1 text-red-600" onClick={() => setAdditionals((arr) => arr.filter((_, j) => j !== i))}>×</button>
          </div>
        ))}
      </div>

      {/* Horarios */}
      <div className="border border-[#E5E2D9] rounded-xl p-4 bg-white">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">Horarios de atención</div>
          <button type="button" onClick={() => setSchedule((s) => [...s, { day: "Lunes", from: "10:00", to: "20:00" }])} className="btn-ghost text-sm">+ Añadir día</button>
        </div>
        {schedule.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
            <select
              data-testid={`sched-day-${i}`}
              className="col-span-4 bg-white border border-[#E5E2D9] rounded-lg px-3 py-2 text-sm"
              value={s.day}
              onChange={(e) => setSchedule((arr) => arr.map((x, j) => j === i ? { ...x, day: e.target.value } : x))}
            >
              {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              <option value="Lun-Vie">Lun a Vie</option>
              <option value="Lun-Dom">Todos los días</option>
              <option value="Sáb-Dom">Fin de semana</option>
            </select>
            <Input type="time" className="col-span-3" value={s.from}
                   onChange={(e) => setSchedule((arr) => arr.map((x, j) => j === i ? { ...x, from: e.target.value } : x))} />
            <span className="col-span-1 text-center text-[#6B6A63]">→</span>
            <Input type="time" className="col-span-3" value={s.to}
                   onChange={(e) => setSchedule((arr) => arr.map((x, j) => j === i ? { ...x, to: e.target.value } : x))} />
            <button className="col-span-1 text-red-600" onClick={() => setSchedule((arr) => arr.filter((_, j) => j !== i))}>×</button>
          </div>
        ))}
        {!schedule.length && <div className="text-xs text-[#6B6A63]">Aún no has agregado horarios.</div>}
      </div>

      <button data-testid="save-profile" onClick={submit} className="btn-primary">Guardar cambios</button>
    </div>
  );
};

export default ModelDashboard;
